// Lightweight rich-text renderer: preserves newlines, supports bullet lists
// (lines starting with `- `, `• `, or `* `), numbered lists (`1. `, `2. `, ...),
// blank-line paragraph breaks, and inline **bold** / *italic* markers.
// No external dependency. Safe: no dangerouslySetInnerHTML.
import React from "react";

const BULLET_RE = /^\s*[-•*]\s+(.*)$/;
const NUMBER_RE = /^\s*\d+\.\s+(.*)$/;

function inline(text, keyPrefix) {
  // Walk through the string and replace **bold** and *italic* / _italic_.
  // Bold takes priority over italic; markers must be non-empty.
  const nodes = [];
  let i = 0;
  let key = 0;
  while (i < text.length) {
    // **bold**
    if (text[i] === "*" && text[i + 1] === "*") {
      const end = text.indexOf("**", i + 2);
      if (end !== -1) {
        nodes.push(<strong key={`${keyPrefix}-b${key++}`}>{text.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    // *italic* or _italic_
    if ((text[i] === "*" || text[i] === "_") && text[i + 1] !== text[i]) {
      const marker = text[i];
      const end = text.indexOf(marker, i + 1);
      if (end !== -1 && end !== i + 1) {
        nodes.push(<em key={`${keyPrefix}-i${key++}`}>{text.slice(i + 1, end)}</em>);
        i = end + 1;
        continue;
      }
    }
    // plain char run until next marker
    let j = i;
    while (j < text.length && text[j] !== "*" && text[j] !== "_") j++;
    if (j === i) j = i + 1; // guarantee progress
    nodes.push(text.slice(i, j));
    i = j;
  }
  return nodes;
}

export default function RichText({ text, className = "" }) {
  if (!text) return null;
  const lines = String(text).replace(/\r\n?/g, "\n").split("\n");

  // Group into blocks. Each block is either { type: 'ul', items }, { type: 'ol', items }, or { type: 'p', lines }.
  const blocks = [];
  let current = null;
  const flush = () => {
    if (current) blocks.push(current);
    current = null;
  };

  for (const raw of lines) {
    if (!raw.trim()) {
      flush();
      continue;
    }
    const bm = raw.match(BULLET_RE);
    const nm = !bm && raw.match(NUMBER_RE);
    if (bm) {
      if (!current || current.type !== "ul") {
        flush();
        current = { type: "ul", items: [] };
      }
      current.items.push(bm[1]);
    } else if (nm) {
      if (!current || current.type !== "ol") {
        flush();
        current = { type: "ol", items: [] };
      }
      current.items.push(nm[1]);
    } else {
      if (!current || current.type !== "p") {
        flush();
        current = { type: "p", lines: [] };
      }
      current.lines.push(raw);
    }
  }
  flush();

  return (
    <div className={className} data-testid="rich-text">
      {blocks.map((b, i) => {
        if (b.type === "ul") {
          return (
            <ul key={i} className="list-disc pl-5 my-3 space-y-1.5 marker:text-gold-deep">
              {b.items.map((item, k) => (
                <li key={k}>{inline(item, `${i}-${k}`)}</li>
              ))}
            </ul>
          );
        }
        if (b.type === "ol") {
          return (
            <ol key={i} className="list-decimal pl-5 my-3 space-y-1.5 marker:text-gold-deep">
              {b.items.map((item, k) => (
                <li key={k}>{inline(item, `${i}-${k}`)}</li>
              ))}
            </ol>
          );
        }
        // paragraph — preserve manual line breaks inside a paragraph
        return (
          <p key={i} className="my-3 leading-relaxed">
            {b.lines.map((ln, k) => (
              <React.Fragment key={k}>
                {k > 0 && <br />}
                {inline(ln, `${i}-${k}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
